import SupportButton from '../../components/atoms/SupportButton/SupportButton';
import { Transaction } from '../../services/types';
import Header from '../../components/organisms/Header/Header';
import Container from '../../components/atoms/Container/Container';
import styles from './UploadPage.module.scss';
import addFile from '../../assets/images/addFile.svg';
import fileIcon from '../../assets/images/fileIcon.svg';
import { useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import useTheme from '../../hooks/useTheme';
import Spinner from '../../components/Spinner';
import Button from '../../components/atoms/Button/Button';
import Alert from '../../components/atoms/Alert/Alert';
import { ButtonGroup } from '../../components/ButtonGroup/ButtonGroup';
import OpenOnPhone from '../../components/OpenOnPhone/OpenOnPhone';
import {
  isReceiptPdfOnly,
  getReceiptUploaderTypes,
} from '../../services/receiptUploadPolicy';
import {
  ReceiptPrepareResult,
  prepareReceipt,
  uploadReceipt as sendReceipt,
} from '../../services/receiptUpload';
import { ReceiptUploadFailureReason } from '../../analytics/events';

// CPAYMENT-1786: this screen predates i18n and keeps its hardcoded Russian, but the situations it
// distinguishes are now the same ones every other screen reports.
const UPLOAD_ERROR_TEXT: Partial<Record<ReceiptUploadFailureReason, string>> = {
  offline: 'Нет соединения с интернетом. Проверьте сеть и повторите попытку.',
  network: 'Не удалось отправить файл. Проверьте соединение и повторите попытку.',
  timeout: 'Не удалось отправить файл. Проверьте соединение и повторите попытку.',
};

interface UploadPageProps {
  transaction: Transaction;
  timeLeft: string;
  ticketId: string;
  onCancel?: () => void;
  onConfirm: (key?: string) => void;
  onBack?: () => void;
  uuid?: string;
  /** CPAYMENT-1616: нейтральный текст при повторной загрузке (документ не приняли). */
  reuploadNotice?: string;
  /** CPAYMENT-1616: идёт проверка платежа — вместо блока загрузки спиннер, кнопки заблокированы. */
  verifying?: boolean;
}

const UploadPage = ({
  transaction,
  timeLeft,
  ticketId,
  onConfirm,
  onBack,
  uuid,
  reuploadNotice,
  verifying = false,
}: UploadPageProps) => {
  const theme = useTheme();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [retryNotice, setRetryNotice] = useState<string>('');

  // Спиннер вместо блока загрузки: свой аплоад в процессе либо идёт авто-проверка чека.
  const busy = loading || verifying;

  const pdfOnly = isReceiptPdfOnly(transaction?.merchant);
  const formatsLabel = pdfOnly ? 'PDF' : 'PDF, JPEG, PNG, JPG';
  const invalidFormatText = pdfOnly
    ? 'Можно загружать только PDF'
    : 'Можно загружать PDF или изображение (JPEG, PNG)';

  const handleChange = async (picked: File) => {
    setLoading(true);
    setRetryNotice('');

    // CPAYMENT-1821: the bytes are pulled out of the picked file here, at selection, and the upload
    // carries the copy. Validation, telemetry and the read all live in `prepareReceipt`.
    // CPAYMENT-1822: and whatever it throws, the screen comes back — a rejected promise here used
    // to leave it spinning forever with nothing reported. This page keeps the library's own file
    // input (react-drag-drop-files), so it gets the safety net but not the pick-step events.
    let prepared: ReceiptPrepareResult;
    try {
      prepared = await prepareReceipt(uuid, transaction, picked);
    } catch {
      prepared = { ok: false, file: null, error: 'unreadable' };
    } finally {
      setLoading(false);
    }

    if (!prepared.ok || !prepared.file) {
      setErrorText(
        prepared.error === 'invalidFormat'
          ? invalidFormatText
          : prepared.error === 'fileTooLarge'
            ? 'Размер файла не должен превышать 5 МБ'
            : prepared.error === 'fileEmpty'
              ? 'Файл пустой, выберите другой'
              : 'Не удалось прочитать файл. Выберите чек заново'
      );
      return;
    }

    setErrorText('');
    setFile(prepared.file);
  }

  // CPAYMENT-1786: same upload service as every other screen. This page has no i18n, so it maps the
  // reason to its own Russian strings, but the classification, retries and reporting are shared.
  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);
    setRetryNotice('');

    if (!file) {
      setLoading(false);
      return;
    }

    setUploadProgress(0);
    try {
      const result = await sendReceipt(uuid || '', transaction, file, {
        // This screen serves both the first upload and the replacement after a failed auto-check
        // (DefaultTemplate passes `reuploadNotice` only in the latter case), and 409 means opposite
        // things in the two: file already stored versus replacement refused.
        isReplacement: !!reuploadNotice,
        onProgress: setUploadProgress,
        onRetryScheduled: () => setRetryNotice('Нет связи — пробуем отправить снова…'),
      });
      setRetryNotice('');

      if (result.ok) {
        onConfirm();
        return;
      }

      setUploadProgress(0);
      setErrorText(
        result.httpStatus === 413
          ? 'Превышен максимальный размер'
          : UPLOAD_ERROR_TEXT[result.reason] ?? 'Произошла ошибка'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.wrapper}>
        <Header
          transaction={transaction}
          ticketId={ticketId}
          timeLeft={timeLeft}
        />
        {transaction?.merchant?.toLowerCase() === 'vocxod' && (
          <div style={{ marginBottom: '28px' }}>
            <SupportButton userId={transaction.externalUserId} />
          </div>
        )}
        <Container>
          <h3 className={styles.title}>
            {verifying
              ? 'Проверяем платёж'
              : reuploadNotice || 'Прикрепите чек об оплате'}
          </h3>
          {busy && (
            <div className={styles.fileBox}>
              <div className={styles.content}>
                <Spinner
                  description={
                    verifying
                      ? 'Это займёт несколько секунд'
                      : uploadProgress > 0
                        ? `Загрузка файла… ${uploadProgress}%`
                        : 'Ожидание файла...'
                  }
                />
              </div>
            </div>
          )}
          {!busy && (
            <FileUploader
              classes='drop_area drop_zone'
              handleChange={handleChange}
              onTypeError={() => setErrorText(invalidFormatText)}
              name='file'
              types={getReceiptUploaderTypes(transaction?.merchant)}
              hoverTitle='Поместите файл здесь'
              dropMessageStyle={{
                backgroundColor: theme === 'dark' ? '#1e2b3ee1' : '#ffffff',
                opacity: 1,
                border: 'none',
                fontSize: '16px',
              }}
            >
              <div className={styles.fileBox}>
                <div className={styles.content}>
                  {!file ? (
                    <>
                      <img
                        className={styles.icon}
                        src={addFile}
                        alt='add_file'
                      />
                      <p>{formatsLabel}</p>
                    </>
                  ) : (
                    <>
                      <img
                        className={styles.icon}
                        src={fileIcon}
                        alt='add_file'
                      />
                      <p className={styles.text}>{file?.name}</p>
                    </>
                  )}
                </div>
              </div>
            </FileUploader>
          )}
          {onBack ? (
            <ButtonGroup>
              <Button
                variant='secondary'
                onClick={onBack}
                disabled={busy}
                dataSentryElement='upload-back'
              >
                Вернуться
              </Button>
              <Button
                variant='primary'
                disabled={!file || busy}
                onClick={handleConfirm}
                dataSentryElement='upload-send'
              >
                Продолжить
              </Button>
            </ButtonGroup>
          ) : (
            <Button
              variant='primary'
              disabled={!file || busy}
              onClick={handleConfirm}
              dataSentryElement='upload-send'
            >
              Продолжить
            </Button>
          )}
          {/* CPAYMENT-1718: пока идёт загрузка или проверка — блок прячем, приоритет у состояния. */}
          {!busy && (
            <OpenOnPhone
              orLabel='или'
              title='Загрузите чек с телефона'
              hint='Наведите камеру на код — откроется эта же страница, приложите чек прямо с телефона'
            />
          )}
        </Container>
      </div>
      {/* CPAYMENT-1786: a retry in flight is neutral — the client has nothing to do but wait. Without
          this the whole backoff was invisible here and the spinner kept the stale percentage. */}
      {retryNotice && !errorText && (
        <div
          style={{
            color: '#8a8a8a',
            fontSize: 13,
            fontFamily: "'PT Mono', monospace",
            textAlign: 'center',
            marginBottom: 12,
          }}
        >
          {retryNotice}
        </div>
      )}
      <Alert text={errorText} onHide={() => setErrorText('')} />
    </>
  );
};

export default UploadPage;
